import { getCountryUrl, getRegionUrl } from '@domain/routes/urls';
import { InternalLink } from '../Clickables/InternalLink/InternalLink';
import { Text } from '../Text/Text';
import { variants } from '../Text/variants';
import { ComponentType, FunctionComponent } from 'react';

interface IProps {
  regionId?: string | null;
  regionName?: string | null;
  regionDisplayName?: string | null;
  countryId?: string;
  countryName?: string;
  countyName?: string;
  className?: string;
  LinkClassName?: string;
  textAs?: string | ComponentType<any> | FunctionComponent<any>;
  textVariant?: keyof typeof variants;
}

/**
 * `LocationLinks` component renders links to a region and country based on provided IDs and names.
 *
 * @param {Object} props - The properties object.
 * @param {string | null} [props.regionId] - The ID of the region (optional).
 * @param {string | null} [props.regionName] - The name of the region (optional).
 * @param {string} props.countryId - The ID of the country.
 * @param {string} props.countryName - The name of the country.
 * @param {string} [props.className] - Additional class names for the `Text` component (optional).
 * @param {string} [props.LinkClassName] - Additional class names for the `InternalLink` component (optional).
 * @param {string | ComponentType<any> | FunctionComponent<any>} [props.textAs='p'] - The HTML tag or component to render the text as (optional).
 * @param {keyof typeof variants} [props.textVariant='xs'] - The variant of the text (optional).
 *
 * @returns {JSX.Element} The rendered `LocationLinks` component.
 */
export const LocationLinks = ({
  regionId,
  regionName,
  regionDisplayName,
  countryId,
  countryName,
  countyName,
  className,
  LinkClassName,
  textAs = 'p',
  textVariant = 'xs',
}: IProps) => {
  return (
    <>
      {countyName && <>{countyName}, </>}
      {countryId && (
        <Text as={textAs} variant={textVariant} className={className}>
          {countryId && regionId && (
            <>
              <InternalLink
                className={LinkClassName}
                to={getRegionUrl(countryId, regionId, countryName ?? '', regionName ?? '')}
              >
                {regionDisplayName ?? regionName}
              </InternalLink>
              <span>{', '}</span>
            </>
          )}
          {countryId && (
            <InternalLink
              className={LinkClassName}
              to={getCountryUrl(countryId, countryName ?? '')}
            >
              {countryName ?? ''}
            </InternalLink>
          )}
        </Text>
      )}
    </>
  );
};
